import React, { useCallback, useState } from 'react'
import { Form } from 'react-bootstrap'

import { useNavigate } from 'react-router-dom'
import { Button } from '../../../components/Button/Button'
import { Modal } from '../../../components/Modal/Modal'
import { authApi, useDeleteOAuth2AppMutation } from '../../../store/auth/auth'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { addToast } from '../../../store/toast/toastSlice'

interface DeleteAppModalProps {
  show: boolean
  clientId: string
  appName: string
  onHide: () => void
}

export const DeleteAppModal: React.FC<DeleteAppModalProps> = ({ show, clientId, appName, onHide }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const accountId = useAppSelector(selectAccountId)
  const [confirmText, setConfirmText] = useState<string>('')
  const [deleteApp, { isLoading }] = useDeleteOAuth2AppMutation()

  const isConfirmed = confirmText === appName

  const handleHide = useCallback(() => {
    setConfirmText('')
    onHide()
  }, [onHide])

  const handleDelete = useCallback(async () => {
    if (!isConfirmed) {
      return
    }
    try {
      await deleteApp({ accountId, clientId }).unwrap()
      dispatch(
        authApi.util.updateQueryData('listOAuth2Apps', { accountId }, (draft) => {
          draft.data = draft.data.filter((app) => app.clientId !== clientId)
        }),
      )
      dispatch(addToast({ variant: 'success', title: `${appName} has been deleted.` }))
      navigate('/account/integration-settings/oauth2apps')
    } catch {
      dispatch(addToast({ variant: 'error', messages: ['Failed to delete the app. Please try again.'] }))
    }
  }, [isConfirmed, deleteApp, accountId, clientId, dispatch, appName, navigate])

  return (
    <Modal
      show={show}
      onHide={handleHide}
      header={`Delete ${appName}?`}
      body={
        <div className='d-flex flex-column gap-3'>
          <p className='mb-0'>This action cannot be undone.</p>
          <div className='d-flex align-items-center zocks-gap-0.75 p-3 rounded-3 zocks-border-color-warning zocks-bg-color-warning zocks-text-gray-900'>
            <i className='bi bi-exclamation-triangle flex-shrink-0' />
            <span>Deleting {appName} will permanently remove the app, revoke all credentials, and disconnect any active connections.</span>
          </div>
          <Form.Group>
            <Form.Label>Type the app name to confirm</Form.Label>
            <Form.Control type='text' placeholder={appName} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} autoComplete='off' />
          </Form.Group>
        </div>
      }
      footer={
        <div className='d-flex gap-3'>
          <Button variant='secondary' onClick={handleHide}>
            Cancel
          </Button>
          <Button variant='danger' disabled={!isConfirmed} loading={isLoading} onClick={handleDelete}>
            <i className='bi bi-trash me-2' />
            Delete app
          </Button>
        </div>
      }
    />
  )
}
